<template>
  <div>
    <BaseToolBar
      title="Équipements"
      subtitle="Liste des équipements installés sur ce véhicule"
    >
      <BaseButton
        class="btn btn-primary ml-3"
        v-tooltip="'Ajouter un équipement'"
        @click="addEquipement"
        :loading="loading"
        icon="plus"
        text="Ajouter"
      ></BaseButton>
    </BaseToolBar>

    <!-- Feedback message -->
    <div v-if="feedback.message" :class="`alert alert-${feedback.type} mt-3`">
      {{ feedback.message }}
    </div>

    <!-- Liste des équipements -->
    <div
      v-for="(equipement, index) in vehicule.equipements"
      :key="equipement.id || 'new_' + index"
      class="card mb-3"
    >
      <div
        class="card-header d-flex justify-content-between align-items-center"
      >
        <h6 class="mb-0">
          Équipement {{ index + 1 }}
          <span class="badge badge-info ml-2">
            {{ getTypeEquipementLabel(equipement.type) }}
          </span>
          <span
            v-if="equipement.date_affectation"
            class="badge badge-secondary ml-2"
          >
            {{ formatDate(equipement.date_affectation) }}
          </span>
        </h6>
        <div>
          <ButtonCircle
            v-if="
              !equipementEnCoursEdition ||
                equipementEnCoursEdition.id !== equipement.id
            "
            name="edit"
            @click="editEquipement(equipement)"
            class="m-1"
            :loading="loading"
            v-tooltip="'Modifier'"
          ></ButtonCircle>
          <ButtonCircle
            name="trash"
            @click="deleteEquipement(equipement)"
            class="m-1"
            :loading="loading"
            v-tooltip="'Supprimer'"
          ></ButtonCircle>
        </div>
      </div>
      <div class="card-body">
        <!-- Vue édition -->
        <div
          v-if="
            equipementEnCoursEdition &&
              equipementEnCoursEdition.id === equipement.id
          "
        >
          <base-form-row row>
            <base-select
              v-model.number="equipementEnCoursEdition.type"
              inputText="Type d'équipement"
              :options="typesEquipement"
            ></base-select>
            <base-inputDatePicker
              v-model="equipementEnCoursEdition.date_affectation"
              inputText="Date d'installation"
            ></base-inputDatePicker>
          </base-form-row>
          <base-form-row>
            <base-input
              v-model="equipementEnCoursEdition.commentaires"
              inputText="Commentaires"
              type="textarea"
              rows="3"
            ></base-input>
          </base-form-row>

          <div class="d-flex justify-content-end mt-3">
            <button
              class="btn btn-secondary mr-2"
              @click="cancelEdit()"
              :disabled="loading"
            >
              Annuler
            </button>
            <button
              class="btn btn-primary"
              @click="saveEquipementEnCours()"
              :disabled="loading"
            >
              <i class="fas fa-save mr-1"></i>
              Enregistrer
            </button>
          </div>
        </div>

        <!-- Vue normale (non-édition) -->
        <div v-else>
          <div class="row mb-3">
            <div class="col-md-6">
              <b>Type:</b> {{ getTypeEquipementLabel(equipement.type) }}
            </div>
            <div class="col-md-6">
              <b>Date d'installation:</b>
              {{
                equipement.date_affectation
                  ? formatDate(equipement.date_affectation)
                  : "Non définie"
              }}
            </div>
          </div>
          <div class="row mb-3" v-if="equipement.commentaires">
            <div class="col-md-12">
              <b>Commentaires:</b> {{ equipement.commentaires }}
            </div>
          </div>
        </div>

        <div class="pt-3" v-if="equipement.id">
          <uploader
            :id="'doc_vehicule_equipement_' + equipement.id"
            url="/upload/document"
            file_system="VEHICULES_EQUIPEMENTS"
            file_field="equipement"
            :file_key="equipement.id"
            title="Pièces jointes"
          ></uploader>
        </div>
      </div>
    </div>

    <div
      v-if="!vehicule.equipements || vehicule.equipements.length === 0"
      class="alert alert-info"
    >
      Ce véhicule n'a pas encore d'équipement enregistré. Cliquez sur "Ajouter"
      pour ajouter un équipement.
    </div>

    <div class="d-flex justify-content-between mt-4">
      <div class="card text-white bg-info mb-3" style="max-width: 18rem;">
        <div class="card-header">Statistiques</div>
        <div class="card-body">
          <h5 class="card-title">
            {{ vehicule.equipements ? vehicule.equipements.length : 0 }}
            équipement(s)
          </h5>
          <p class="card-text">
            Chaînes: {{ countEquipementByType(0) }}<br />
            Barres de toit: {{ countEquipementByType(1) }}<br />
            Autres: {{ countEquipementByType(2) }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { errHandler } from "@/helpers.js";
import BaseToolBar from "@/components/bases/ToolBar.vue";
import BaseFormRow from "@/components/bases/FormRow.vue";
import BaseInput from "@/components/bases/Input.vue";
import BaseSelect from "@/components/bases/Select.vue";
import BaseInputDatePicker from "@/components/bases/InputDatePicker.vue";
import BaseButton from "@/components/bases/Button.vue";
import ButtonCircle from "@/components/bases/ButtonCircle.vue";
import Uploader from "@/components/bases/Uploader.vue";

export default {
  name: "VehiculeEquipement",
  components: {
    BaseToolBar,
    BaseFormRow,
    BaseInput,
    BaseSelect,
    BaseInputDatePicker,
    BaseButton,
    ButtonCircle,
    Uploader
  },
  props: {
    vehicule: {
      type: Object,
      required: true
    },
    statut: {
      type: String,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      equipementEnCoursEdition: null,
      isLoading: false,
      feedback: {
        message: "",
        type: "info"
      },
      typesEquipement: [
        { id: 0, description: "Chaînes" },
        { id: 1, description: "Barres de toit" },
        { id: 2, description: "Autre" }
      ]
    };
  },
  mounted() {
    // Initialiser la liste des équipements si elle n'existe pas
    if (!this.vehicule.equipements) {
      this.$set(this.vehicule, "equipements", []);
    }
  },
  methods: {
    addEquipement() {
      // Vérifier que le véhicule est bien enregistré
      if (!this.vehicule.id) {
        this.showFeedback(
          "Veuillez d'abord enregistrer le véhicule",
          "warning"
        );
        return;
      }

      // Fermer toute édition en cours
      this.equipementEnCoursEdition = null;

      // Créer immédiatement un équipement en base de données
      this.isLoading = true;

      this.$http
        .post("/vehicules-equipement", { vehicule_id: this.vehicule.id })
        .then(response => {
          // Vérifier que equipements est initialisé
          if (!this.vehicule.equipements) {
            this.$set(this.vehicule, "equipements", []);
          }

          // Ajouter l'équipement retourné par l'API
          this.vehicule.equipements.unshift(response.data);

          // Passer en mode édition sur ce nouvel équipement
          this.editEquipement(response.data);

          this.showFeedback(
            "Équipement créé avec succès. Vous pouvez maintenant compléter les détails.",
            "success"
          );
        })
        .catch(error => {
          console.error(error);
          this.showFeedback(errHandler(error), "danger");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    editEquipement(equipement) {
      // Créer une copie pour ne pas modifier directement l'original
      this.equipementEnCoursEdition = { ...equipement };
    },
    cancelEdit() {
      this.equipementEnCoursEdition = null;
    },
    saveEquipementEnCours() {
      if (!this.equipementEnCoursEdition || !this.equipementEnCoursEdition.id)
        return;

      this.isLoading = true;
      this.$http
        .put(
          "/vehicules-equipement/" + this.equipementEnCoursEdition.id,
          this.equipementEnCoursEdition
        )
        .then(response => {
          // Mettre à jour l'équipement dans le tableau
          const index = this.vehicule.equipements.findIndex(
            e => e.id === this.equipementEnCoursEdition.id
          );
          if (index !== -1) {
            this.$set(this.vehicule.equipements, index, response.data);
          }
          this.equipementEnCoursEdition = null;
          this.showFeedback("Équipement mis à jour avec succès", "success");
        })
        .catch(error => {
          console.error(error);
          this.showFeedback(errHandler(error), "danger");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    deleteEquipement(equipement) {
      if (
        !window.confirm("Êtes-vous sûr de vouloir supprimer cet équipement ?")
      ) {
        return;
      }

      // Si c'est un nouvel équipement non encore sauvegardé
      if (!equipement.id) {
        const index = this.vehicule.equipements.findIndex(
          e => e === equipement
        );
        if (index !== -1) {
          this.vehicule.equipements.splice(index, 1);
        }
        return;
      }

      this.isLoading = true;
      this.$http
        .delete(`/vehicules-equipement/${equipement.id}`)
        .then(() => {
          const index = this.vehicule.equipements.findIndex(
            e => e.id === equipement.id
          );
          if (index !== -1) {
            this.vehicule.equipements.splice(index, 1);
          }
          this.showFeedback("Équipement supprimé avec succès", "success");
        })
        .catch(error => {
          console.error(error);
          this.showFeedback(errHandler(error), "danger");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    getTypeEquipementLabel(typeId) {
      const type = this.typesEquipement.find(t => t.id === parseInt(typeId));
      return type ? type.description : "Non défini";
    },
    countEquipementByType(typeId) {
      if (!this.vehicule.equipements) return 0;
      return this.vehicule.equipements.filter(e => parseInt(e.type) === typeId)
        .length;
    },
    formatDate(date) {
      if (!date) return "";
      return this.$moment(date).format("DD/MM/YYYY");
    },
    showFeedback(message, type = "info") {
      this.feedback = { message, type };
      // Faire disparaître le message après quelques secondes
      setTimeout(() => {
        this.feedback = { message: "", type: "info" };
      }, 3000);
    }
  }
};
</script>

<style scoped>
.card-header {
  background-color: #f8f9fa;
}

.badge-info {
  background-color: #17a2b8;
  color: #fff;
}

.badge-secondary {
  background-color: #6c757d;
  color: #fff;
}

.border-primary {
  border-color: #007bff !important;
}

.bg-primary {
  background-color: #007bff !important;
}

.text-white {
  color: white !important;
}
</style>
